export const PAX_SESSION_STORAGE_KEY = 'pax_session';

export const PAX_SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface PaxMagicCustomer {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

export interface PaxMagicSession {
  token: string;
  customer: PaxMagicCustomer;
  verifiedAt: string;
}

export interface PaxSessionVerificationResult {
  success: boolean;
  customer?: PaxMagicCustomer;
  error?: string;
}

export type PaxSessionVerifier = (token: string) => Promise<PaxSessionVerificationResult>;

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parsePaxSession(raw: string | null): PaxMagicSession | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) return null;

    const { token, customer, verifiedAt } = parsed;
    if (typeof token !== 'string' || token.length === 0) return null;
    if (typeof verifiedAt !== 'string' || Number.isNaN(new Date(verifiedAt).getTime())) return null;
    if (!isRecord(customer) || typeof customer.id !== 'string' || customer.id.length === 0) return null;

    return {
      token,
      verifiedAt,
      customer: {
        id: customer.id,
        email: typeof customer.email === 'string' ? customer.email : null,
        first_name: typeof customer.first_name === 'string' ? customer.first_name : null,
        last_name: typeof customer.last_name === 'string' ? customer.last_name : null,
      },
    };
  } catch {
    return null;
  }
}

export function isFreshPaxSession(
  session: Pick<PaxMagicSession, 'verifiedAt'>,
  now = Date.now(),
): boolean {
  const verifiedAt = new Date(session.verifiedAt).getTime();
  return Number.isFinite(verifiedAt) && now - verifiedAt < PAX_SESSION_MAX_AGE_MS;
}

export function getStoredPaxSession(storage: Storage | null = getLocalStorage()): PaxMagicSession | null {
  if (!storage) return null;
  const session = parsePaxSession(storage.getItem(PAX_SESSION_STORAGE_KEY));
  if (!session || !isFreshPaxSession(session)) {
    storage.removeItem(PAX_SESSION_STORAGE_KEY);
    return null;
  }
  return session;
}

export function savePaxSession(
  token: string,
  customer: PaxMagicCustomer,
  storage: Storage | null = getLocalStorage(),
): PaxMagicSession {
  const session: PaxMagicSession = {
    token,
    customer,
    verifiedAt: new Date().toISOString(),
  };

  storage?.setItem(PAX_SESSION_STORAGE_KEY, JSON.stringify(session));
  return session;
}

export function clearPaxSession(storage: Storage | null = getLocalStorage()) {
  storage?.removeItem(PAX_SESSION_STORAGE_KEY);
}

export async function verifyStoredPaxSession(
  verifier: PaxSessionVerifier,
  storage: Storage | null = getLocalStorage(),
): Promise<PaxMagicSession | null> {
  const session = getStoredPaxSession(storage);
  if (!session) return null;

  const verified = await verifier(session.token);
  if (!verified.success || !verified.customer) {
    clearPaxSession(storage);
    return null;
  }

  return savePaxSession(session.token, verified.customer, storage);
}
